import { useCallback } from "react";
import {
	type MetaAdsCityTarget,
	type MetaAdsDMATarget,
	type MetaAdsGeoExclusions,
	type MetaAdsGeoTargeting,
	MetaAdsGeoTargetingMode,
	type MetaAdsGeoTemplate,
	type MetaAdsLocationPin,
	MetaAdsLocationType,
	type MetaAdsRegionTarget,
	type MetaAdsZipCodeTarget,
} from "shared";
import { useCampaignWizard } from "./CampaignWizardContext";

const MAX_PINS = 10;

const EMPTY_EXCLUSIONS: MetaAdsGeoExclusions = {
	zipCodes: [],
	cities: [],
	regions: [],
	customLocations: [],
};

export function useGeoTargeting() {
	const { draft, updateDraft } = useCampaignWizard();

	const geo: MetaAdsGeoTargeting = draft.geoTargeting ?? {
		locationPins: [],
		locationType: MetaAdsLocationType.HOME_RECENT,
		countries: draft.targetCountries ?? ["US"],
	};

	const mode = geo.mode ?? MetaAdsGeoTargetingMode.RADIUS_PIN;
	const exclusions = geo.exclusions ?? EMPTY_EXCLUSIONS;

	const update = useCallback(
		(partial: Partial<MetaAdsGeoTargeting>) => {
			updateDraft({
				geoTargeting: { ...geo, ...partial },
			});
		},
		[geo, updateDraft],
	);

	// ── Mode ──

	const setMode = useCallback(
		(newMode: MetaAdsGeoTargetingMode) => {
			update({ mode: newMode });
		},
		[update],
	);

	// ── Radius Pins (existing) ──

	const addPin = useCallback(
		(pin: Omit<MetaAdsLocationPin, "id">) => {
			if (geo.locationPins.length >= MAX_PINS) return;
			const newPin: MetaAdsLocationPin = {
				...pin,
				id: crypto.randomUUID(),
			};
			update({ locationPins: [...geo.locationPins, newPin] });
		},
		[geo.locationPins, update],
	);

	const removePin = useCallback(
		(pinId: string) => {
			update({
				locationPins: geo.locationPins.filter((p) => p.id !== pinId),
			});
		},
		[geo.locationPins, update],
	);

	const updatePin = useCallback(
		(pinId: string, updates: Partial<MetaAdsLocationPin>) => {
			update({
				locationPins: geo.locationPins.map((p) =>
					p.id === pinId ? { ...p, ...updates } : p,
				),
			});
		},
		[geo.locationPins, update],
	);

	const setLocationType = useCallback(
		(locationType: MetaAdsLocationType) => {
			update({ locationType });
		},
		[update],
	);

	const canAddPin = geo.locationPins.length < MAX_PINS;

	// ── Zip Codes ──

	const addZipCode = useCallback(
		(zip: MetaAdsZipCodeTarget) => {
			const existing = geo.zipCodes ?? [];
			if (existing.some((z) => z.key === zip.key)) return;
			update({ zipCodes: [...existing, zip] });
		},
		[geo.zipCodes, update],
	);

	const removeZipCode = useCallback(
		(key: string) => {
			update({
				zipCodes: (geo.zipCodes ?? []).filter((z) => z.key !== key),
			});
		},
		[geo.zipCodes, update],
	);

	const addZipCodes = useCallback(
		(zips: MetaAdsZipCodeTarget[]) => {
			const existing = geo.zipCodes ?? [];
			const existingKeys = new Set(existing.map((z) => z.key));
			const newZips = zips.filter((z) => !existingKeys.has(z.key));
			if (newZips.length === 0) return;
			update({ zipCodes: [...existing, ...newZips] });
		},
		[geo.zipCodes, update],
	);

	// ── Cities ──

	const addCity = useCallback(
		(city: MetaAdsCityTarget) => {
			const existing = geo.cities ?? [];
			if (existing.some((c) => c.key === city.key)) return;
			update({ cities: [...existing, city] });
		},
		[geo.cities, update],
	);

	const removeCity = useCallback(
		(key: string) => {
			update({
				cities: (geo.cities ?? []).filter((c) => c.key !== key),
			});
		},
		[geo.cities, update],
	);

	// ── Regions ──

	const addRegion = useCallback(
		(region: MetaAdsRegionTarget) => {
			const existing = geo.regions ?? [];
			if (existing.some((r) => r.key === region.key)) return;
			update({ regions: [...existing, region] });
		},
		[geo.regions, update],
	);

	const removeRegion = useCallback(
		(key: string) => {
			update({
				regions: (geo.regions ?? []).filter((r) => r.key !== key),
			});
		},
		[geo.regions, update],
	);

	// ── DMAs ──

	const addDMA = useCallback(
		(dma: MetaAdsDMATarget) => {
			const existing = geo.dmas ?? [];
			if (existing.some((d) => d.key === dma.key)) return;
			update({ dmas: [...existing, dma] });
		},
		[geo.dmas, update],
	);

	const removeDMA = useCallback(
		(key: string) => {
			update({
				dmas: (geo.dmas ?? []).filter((d) => d.key !== key),
			});
		},
		[geo.dmas, update],
	);

	// ── Exclusions ──

	const updateExclusions = useCallback(
		(partial: Partial<MetaAdsGeoExclusions>) => {
			update({ exclusions: { ...exclusions, ...partial } });
		},
		[exclusions, update],
	);

	const addExclusionZip = useCallback(
		(zip: MetaAdsZipCodeTarget) => {
			if (exclusions.zipCodes.some((z) => z.key === zip.key)) return;
			updateExclusions({ zipCodes: [...exclusions.zipCodes, zip] });
		},
		[exclusions.zipCodes, updateExclusions],
	);

	const removeExclusionZip = useCallback(
		(key: string) => {
			updateExclusions({
				zipCodes: exclusions.zipCodes.filter((z) => z.key !== key),
			});
		},
		[exclusions.zipCodes, updateExclusions],
	);

	const addExclusionCity = useCallback(
		(city: MetaAdsCityTarget) => {
			if (exclusions.cities.some((c) => c.key === city.key)) return;
			updateExclusions({ cities: [...exclusions.cities, city] });
		},
		[exclusions.cities, updateExclusions],
	);

	const removeExclusionCity = useCallback(
		(key: string) => {
			updateExclusions({
				cities: exclusions.cities.filter((c) => c.key !== key),
			});
		},
		[exclusions.cities, updateExclusions],
	);

	const addExclusionRegion = useCallback(
		(region: MetaAdsRegionTarget) => {
			if (exclusions.regions.some((r) => r.key === region.key)) return;
			updateExclusions({ regions: [...exclusions.regions, region] });
		},
		[exclusions.regions, updateExclusions],
	);

	const removeExclusionRegion = useCallback(
		(key: string) => {
			updateExclusions({
				regions: exclusions.regions.filter((r) => r.key !== key),
			});
		},
		[exclusions.regions, updateExclusions],
	);

	const addExclusionPin = useCallback(
		(pin: Omit<MetaAdsLocationPin, "id">) => {
			const newPin: MetaAdsLocationPin = {
				...pin,
				id: crypto.randomUUID(),
			};
			updateExclusions({
				customLocations: [...exclusions.customLocations, newPin],
			});
		},
		[exclusions.customLocations, updateExclusions],
	);

	const removeExclusionPin = useCallback(
		(pinId: string) => {
			updateExclusions({
				customLocations: exclusions.customLocations.filter(
					(p) => p.id !== pinId,
				),
			});
		},
		[exclusions.customLocations, updateExclusions],
	);

	// ── Templates ──

	const applyTemplate = useCallback(
		(template: MetaAdsGeoTemplate) => {
			updateDraft({
				geoTargeting: { ...template.geoTargeting },
			});
		},
		[updateDraft],
	);

	return {
		geo,
		mode,
		setMode,
		addPin,
		removePin,
		updatePin,
		setLocationType,
		canAddPin,
		addZipCode,
		removeZipCode,
		addZipCodes,
		addCity,
		removeCity,
		addRegion,
		removeRegion,
		addDMA,
		removeDMA,
		exclusions,
		addExclusionZip,
		removeExclusionZip,
		addExclusionCity,
		removeExclusionCity,
		addExclusionRegion,
		removeExclusionRegion,
		addExclusionPin,
		removeExclusionPin,
		applyTemplate,
	};
}
